// src/AddPartnerForm.js
import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth, db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
    TextField, Button, Select, MenuItem, FormControl, InputLabel,
    FormGroup, FormControlLabel, Checkbox, Typography, Box, Grid, Paper, Alert,
    Chip, Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MaskedInput from './MaskedInput';

// Mapa de estados → URL de consulta pública do CRECI
const CRECI_URLS = {
    SP: 'https://www.crecisp.gov.br/cidadao/buscaporcorretores',
    DEFAULT: 'https://www.crecisp.gov.br/cidadao/buscaporcorretores',
};

function getCreciUrl(creci) {
    const uf = creci.trim().toUpperCase().replace(/[^A-Z]/g, '').substring(0, 2);
    return CRECI_URLS[uf] || CRECI_URLS.DEFAULT;
}

const ZONAS = [
    "Saúde", "Morumbi", "Aclimação", "Ipiranga", "Mooca", "Carrão", 
    "Itaquera", "Guarulhos", "Santo André", "São Bernardo do Campo", 
    "São Caetano do Sul", "Litoral"
];

function AddPartnerForm({ onPartnerAdded, gerentes }) {
    const [currentUserProfile, setCurrentUserProfile] = useState(null);

    useEffect(() => {
        const fetchProfile = async () => {
            if (auth.currentUser) {
                const docSnap = await getDoc(doc(db, "usuarios", auth.currentUser.uid));
                if (docSnap.exists()) {
                    const profile = docSnap.data();
                    setCurrentUserProfile({ uid: auth.currentUser.uid, ...profile });
                    
                    if (profile.tipoUsuario === 'gerente') {
                        setFormData(prev => ({ ...prev, gerenteId: auth.currentUser.uid }));
                    }
                }
            }
        };
        fetchProfile();
    }, []);

    const initialState = {
        nome: '', email: '', cnpj: '', cpf: '', rg: '', endereco: '', creci: '', telefone: '',
        tipoUsuario: 'autonomo', zonaAtuacao: [], status: 'ativo', gerenteId: '', observacoesInternas: ''
    };
    const [formData, setFormData] = useState(initialState);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    // ── Estado da validação manual do CRECI ──────────────────────────────────
    const [creciValidadoManualmente, setCreciValidadoManualmente] = useState(false);

    const handleChange = (e) => {
        setFormData(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
        // Ao alterar o campo CRECI, reseta a confirmação de validação
        if (e.target.name === 'creci') {
            setCreciValidadoManualmente(false);
        }
    };

    const handleAbrirConsultaCreci = () => {
        if (!formData.creci || formData.creci.trim() === '') {
            setError('Informe o número do CRECI antes de consultar.');
            return;
        }
        const url = getCreciUrl(formData.creci);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleConfirmarValidacao = () => {
        setCreciValidadoManualmente(true);
    };

    const handleZoneChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            zonaAtuacao: checked ? [...prevState.zonaAtuacao, value] : prevState.zonaAtuacao.filter(zone => zone !== value)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setMessage('');
        
        if (formData.tipoUsuario !== 'administrador' && formData.tipoUsuario !== 'gerente') {
             if (formData.zonaAtuacao.length === 0) {
                setError("Selecione pelo menos uma zona de atuação.");
                return;
            }
            if (!formData.gerenteId) {
                setError("A seleção de um gerente é obrigatória.");
                return;
            }
        }

        try {
            const createPartnerFunction = httpsCallable(functions, 'createPartner');
            const agora = new Date().toISOString();
            const dadosParaSalvar = {
                ...formData,
                creciValidado: creciValidadoManualmente,
                creciValidadoEm: creciValidadoManualmente ? agora : null,
                creciValidadoPor: creciValidadoManualmente
                    ? (currentUserProfile?.email || currentUserProfile?.nome || 'Desconhecido')
                    : null,
            };
            await createPartnerFunction(dadosParaSalvar);
            setMessage('Usuário cadastrado com sucesso!');
            const resetState = { ...initialState };
            if (currentUserProfile?.tipoUsuario === 'gerente') {
                resetState.gerenteId = currentUserProfile.uid;
            }
            setFormData(resetState);
            setCreciValidadoManualmente(false);
            onPartnerAdded();
        } catch (err) {
            setError('Falha ao cadastrar: ' + err.message);
        }
    };

    const isLoggedUserAdmin = currentUserProfile?.tipoUsuario === 'administrador';
    const isCreatingAdminUser = formData.tipoUsuario === 'administrador' || formData.tipoUsuario === 'gerente';

    return (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>Cadastrar Novo Usuário</Typography>
            <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Tipo de Usuário</InputLabel>
                            <Select name="tipoUsuario" value={formData.tipoUsuario} label="Tipo de Usuário" onChange={handleChange}>
                                <MenuItem value="autonomo">Parceiro Autônomo</MenuItem>
                                <MenuItem value="imobiliaria">Parceiro Imobiliária</MenuItem>
                                {isLoggedUserAdmin && <MenuItem value="gerente">Gerente</MenuItem>}
                                {isLoggedUserAdmin && <MenuItem value="administrador" sx={{ fontWeight: 'bold', color: 'primary.main' }}>Administrador do Sistema</MenuItem>}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth name="nome" label="Nome completo / Razão Social" value={formData.nome} onChange={handleChange} required /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth name="email" type="email" label="E-mail (login)" value={formData.email} onChange={handleChange} required helperText="Uma senha temporária será gerada automaticamente e enviada por e-mail ao parceiro." /></Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required name="telefone" label="Telefone / WhatsApp" value={formData.telefone} onChange={handleChange} InputProps={{ inputComponent: MaskedInput, inputProps: { mask: '(00) 00000-0000' } }} />
                    </Grid>

                    {!isCreatingAdminUser && (
                        <>
                            {formData.tipoUsuario === 'autonomo' && (
                                <>
                                    <Grid item xs={12} sm={6}><TextField fullWidth name="cpf" label="CPF (Opcional)" value={formData.cpf} onChange={handleChange} InputProps={{ inputComponent: MaskedInput, inputProps: { mask: '000.000.000-00' } }} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth name="rg" label="RG (Opcional)" value={formData.rg} onChange={handleChange} InputProps={{ inputComponent: MaskedInput, inputProps: { mask: '00.000.000-0' } }} /></Grid>
                                </>
                            )}
                            {formData.tipoUsuario === 'imobiliaria' && (
                                <Grid item xs={12} sm={6}><TextField fullWidth name="cnpj" label="CNPJ (Opcional)" value={formData.cnpj} onChange={handleChange} InputProps={{ inputComponent: MaskedInput, inputProps: { mask: '00.000.000/0000-00' } }} /></Grid>
                            )}
                            
                            {/* ── Campo CRECI com consulta manual e confirmação ─────────── */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    name="creci"
                                    label="CRECI"
                                    value={formData.creci}
                                    onChange={handleChange}
                                    required
                                    placeholder="Ex: SP123456F"
                                />
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                    {/* Botão 1: Abre o site do CRECI para consulta */}
                                    <Tooltip title="Abre o site do CRECI SP em nova aba para você verificar manualmente">
                                        <span>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<OpenInNewIcon />}
                                                onClick={handleAbrirConsultaCreci}
                                                disabled={!formData.creci}
                                            >
                                                Consultar CRECI SP
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    {/* Botão 2: Confirma que verificou e está ativo */}
                                    {!creciValidadoManualmente ? (
                                        <Tooltip title="Após consultar o site e confirmar que está ativo, clique aqui">
                                            <span>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="success"
                                                    startIcon={<CheckCircleIcon />}
                                                    onClick={handleConfirmarValidacao}
                                                    disabled={!formData.creci}
                                                >
                                                    Confirmar como Ativo
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Chip
                                            icon={<CheckCircleIcon />}
                                            label={`✓ CRECI validado em ${new Date().toLocaleDateString('pt-BR')}`}
                                            color="success"
                                            variant="outlined"
                                            size="small"
                                            onDelete={() => setCreciValidadoManualmente(false)}
                                        />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth name="endereco" label="Endereço (Opcional)" value={formData.endereco} onChange={handleChange} /></Grid>
                            
                            {/* CORREÇÃO DO LAYOUT DO GERENTE: Ocupa a largura toda e tem minWidth */}
                            {isLoggedUserAdmin && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth required sx={{ minWidth: 200 }}>
                                        <InputLabel>Gerente Responsável</InputLabel>
                                        <Select name="gerenteId" value={formData.gerenteId} label="Gerente Responsável" onChange={handleChange}>
                                            <MenuItem value="">-- Nenhum Gerente --</MenuItem>
                                            {gerentes && gerentes.map(g => (<MenuItem key={g.id} value={g.id}>{g.nome}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            
                            <Grid item xs={12}><FormControl component="fieldset" variant="standard"><Typography component="legend">Zona de Atuação:</Typography><FormGroup row>{ZONAS.map(zona => <FormControlLabel key={zona} control={<Checkbox checked={formData.zonaAtuacao.includes(zona)} onChange={handleZoneChange} value={zona} />} label={zona} />)}</FormGroup></FormControl></Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    name="observacoesInternas"
                                    label="Observações Internas (Visível apenas para Admins e Gerentes)"
                                    value={formData.observacoesInternas}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </>
                    )}
                    
                    <Grid item xs={12}><Button type="submit" variant="contained" fullWidth>Cadastrar Usuário</Button></Grid>
                    {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
                    {message && <Grid item xs={12}><Alert severity="success">{message}</Alert></Grid>}
                </Grid>
            </Box>
        </Paper>
    );
}

export default AddPartnerForm;